import { useEffect, useMemo, useRef, useState } from "react";
import {
  Button,
  Col,
  Form,
  Modal,
  Row,
  Table,
  Input,
  Checkbox,
  notification,
  message,
} from "antd";

import { MealCategory } from "../Models/MealCategory";

import { mealCategoryService } from "../Services/MealCategory.service";
import { CheckOutlined, DownOutlined, UpOutlined } from "@ant-design/icons";
import { useForm } from "antd/es/form/Form";

export function MealCategoriesPage() {
  const [updateFormInstance] = useForm();

  const createForm: any = useRef({});
  const updateForm: any = useRef({});

  const [dataSource, setDataSource] = useState<MealCategory[]>([]);

  const [isShowingCreateModal, setIsShowingCreateModal] = useState(false);
  const [isShowingUpdateModal, setIsShowingUpdateModal] = useState<
    number | null
  >(null);
  const [isFetching, setIsFetching] = useState(false);
  const [filterQuery, setFilterQuery] = useState("");

  const filteredDatasource = useMemo(() => {
    return dataSource.filter((mealCategory) =>
      mealCategory.name.toLowerCase().includes(filterQuery.toLowerCase())
    );
  }, [dataSource, filterQuery]);

  const activeUpdateModel = useMemo(
    () =>
      dataSource.find(
        (mealCategory) => mealCategory.id === isShowingUpdateModal
      ),
    [dataSource, isShowingUpdateModal]
  );

  useEffect(() => {
    fetchData();
  }, []);

  async function fetchData() {
    setIsFetching(true);

    try {
      const mealCategories: MealCategory[] =
        await mealCategoryService.getMealCategories();
      setDataSource(mealCategories);
      setIsFetching(false);
    } catch (error) {
      setIsFetching(false);
    }
  }

  const columns = [
    {
      title: "Navn",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Rækkefølge",
      dataIndex: "order",
      key: "order",
      align: "center" as any,
      width: 80,
    },
    {
      title: "Op",
      dataIndex: "",
      key: "moveUp",
      align: "center" as any,
      width: 40,
      render: (_: any, record: any) => (
        <Button
          type="link"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            // Handle move up action
            if (record.order === 0) return;
            moveUp(record);
          }}
        >
          <UpOutlined />
        </Button>
      ),
    },
    {
      title: "Ned",
      dataIndex: "",
      key: "moveDown",
      align: "center" as any,
      width: 40,
      render: (_: any, record: any) => (
        <Button
          type="link"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();

            if (record.order + 1 > dataSource.length - 1) return;
            // Handle move down action
            moveDown(record);
          }}
        >
          <DownOutlined />
        </Button>
      ),
    },
  ];

  /**
   * Update modal
   */

  function openUpdateModal(id: number) {
    setIsShowingUpdateModal(id);

    const activeIngredient = dataSource.find(
      (ingredient) => ingredient.id === id
    );

    updateFormInstance.setFieldsValue(activeIngredient);
  }

  function closeUpdateModal() {
    setIsShowingUpdateModal(null);
    updateFormInstance.resetFields();
  }

  /**
   * Create modal
   */
  function openCreateModal() {
    setIsShowingCreateModal(true);
  }

  function closeCreateModal() {
    setIsShowingCreateModal(false);
    createForm.current.resetFields();
  }

  function handleCreateAction() {
    const form = createForm.current;
    form.submit();
  }

  async function moveUp(currentItem: MealCategory) {
    const mappedData = dataSource.map((mealCategory) => {
      if (mealCategory.id === currentItem.id) {
        return {
          ...mealCategory,
          order: mealCategory.order - 1,
        };
      }

      if (mealCategory.order === currentItem.order - 1) {
        return {
          ...mealCategory,
          order: mealCategory.order + 1,
        };
      }

      return mealCategory;
    });

    await mealCategoryService.updateMealCategory(mappedData);
    await fetchData();
  }

  async function moveDown(currentItem: MealCategory) {
    const mappedData = dataSource.map((mealCategory) => {
      if (mealCategory.id === currentItem.id) {
        return {
          ...mealCategory,
          order: mealCategory.order + 1,
        };
      }

      if (mealCategory.order === currentItem.order + 1) {
        return {
          ...mealCategory,
          order: mealCategory.order - 1,
        };
      }

      return mealCategory;
    });

    await mealCategoryService.updateMealCategory(mappedData);
    await fetchData();
  }

  async function onUpdateSubmit(values: any) {
    try {
      const success = await mealCategoryService.updateMealCategory([values]);

      if (!!success) {
        await fetchData();
        closeUpdateModal();

        notification.open({
          message: "Sådan!",
          description: "Måltidskategorien er blevet opdateret",
        });
      }
    } catch (error) {
      throw error;
    }
  }

  function onUpdateSubmitFailed(errorInfo: any) {
    console.error("Failed:", errorInfo);
  }

  async function onCreateSubmit(values: any) {
    values.order = dataSource.length;
    const success = await mealCategoryService.createMealCategory(values);

    if (!!success) {
      notification.open({
        message: "Sådan!",
        description: "Måltidskategorien er blevet oprettet",
      });

      await fetchData();
      closeCreateModal();
    }
  }

  function onCreateSubmitFailed(errorInfo: any) {
    console.error("Failed:", errorInfo);
  }

  async function onDelete() {
    if (!activeUpdateModel?.id) return;

    const success = await mealCategoryService.deleteMealCategory(activeUpdateModel, dataSource);

    if (!!success) {
      closeUpdateModal();
      fetchData();

      message.success(`"${activeUpdateModel?.name}" er blevet slettet`);
    }
  }

  const handleFilterQuerychange = (event: any) => {
    setFilterQuery(event.target.value);
  };

  return (
    <>
      <Row>
        <Col flex={1}>
          <Input
            placeholder="Find måltidskategori"
            value={filterQuery}
            onChange={handleFilterQuerychange}
          />
        </Col>
        <Col offset={1}>
          <Button type="primary" onClick={openCreateModal}>
            Opret Måltidskategori
          </Button>
        </Col>
      </Row>

      <Row style={{ marginTop: "16px" }}>
        <Col span={24}>
          <Table
            bordered={true}
            dataSource={filteredDatasource}
            columns={columns}
            loading={isFetching}
            rowKey={(record) => record.id.toString()}
            onRow={(record, rowIndex) => {
              return {
                onClick: (event) => {
                  openUpdateModal(record.id);
                },
              };
            }}
          />
        </Col>
      </Row>

      {/* Update modal */}

      <Modal
        title={`Rediger "${activeUpdateModel?.name}"`}
        open={!!isShowingUpdateModal}
        okText="Opdater"
        cancelText="Annuller"
        onCancel={closeUpdateModal}
        footer={[
          <Row key="meal-categories-update-footer">
            <Button
              key="delete"
              danger
              onClick={() => {
                Modal.confirm({
                  title: "Er du sikker?",
                  content: `Er du sikker på, at du vil slette "${activeUpdateModel?.name}"?`,
                  okText: "Ja, jeg er sikker",
                  cancelText: "Annuller",
                  onOk: () => {
                    onDelete();
                  },
                });
              }}
            >
              Slet
            </Button>
            <span key="spacer" style={{ flex: 1 }} />
            <Button
              key="cancel"
              onClick={() => {
                closeUpdateModal();
              }}
            >
              Annuller
            </Button>
            <Button
              key="update"
              type="primary"
              onClick={() => {
                onUpdateSubmit(updateFormInstance.getFieldsValue());
              }}
            >
              Opdater
            </Button>
          </Row>,
        ]}
      >
        <Form
          form={updateFormInstance}
          ref={updateForm}
          name="update-data"
          labelCol={{ span: 8 }}
          wrapperCol={{ span: 16 }}
          style={{ maxWidth: 600 }}
          onFinish={onUpdateSubmit}
          onFinishFailed={onUpdateSubmitFailed}
          layout="vertical"
          autoComplete="off"
          initialValues={activeUpdateModel}
        >
          <Form.Item hidden={true} name="id">
            <Input disabled={true} />
          </Form.Item>
          {/* Name */}
          <Form.Item
            label="Navn"
            name="name"
            rules={[{ required: true, message: "Feltet skal udfyldes" }]}
          >
            <Input />
          </Form.Item>

          {/* Order */}
          <Form.Item hidden={true} name="order">
            <Input disabled={true} />
          </Form.Item>
        </Form>
      </Modal>

      {/* Create modal */}
      <Modal
        title="Opret måltidskategori"
        open={isShowingCreateModal}
        okText="Opret"
        cancelText="Annuller"
        onOk={handleCreateAction}
        // confirmLoading={confirmLoading}
        onCancel={closeCreateModal}
      >
        <Form
          ref={createForm}
          name="create-data"
          labelCol={{ span: 8 }}
          wrapperCol={{ span: 16 }}
          style={{ maxWidth: 600 }}
          onFinish={onCreateSubmit}
          onFinishFailed={onCreateSubmitFailed}
          layout="vertical"
          autoComplete="off"
        >
          {/* Name */}
          <Form.Item
            label="Navn"
            name="name"
            rules={[{ required: true, message: "Feltet skal udfyldes" }]}
          >
            <Input />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}
